import React, { useEffect, useState } from 'react';
import { Truck, Download, Printer } from 'lucide-react';
import apiService from '../../services/api';

interface RemitoItem { productName: string; productColor: string; quantity: number; packs: number; looseBags: number; packSize: number; }
interface RemitoRow {
  id: number;
  receiptNumber: string;
  customerName: string;
  concept: string;
  items: RemitoItem[];
  createdAt: string;
}

const colorStyle = (colorName: string): React.CSSProperties => {
  const color = String(colorName || '').toLowerCase();
  if (color.includes('negr')) return { backgroundColor: '#111827', color: '#ffffff' };
  if (color.includes('roj')) return { backgroundColor: '#fee2e2', color: '#991b1b' };
  if (color.includes('verde')) return { backgroundColor: '#dcfce7', color: '#166534' };
  if (color.includes('azul')) return { backgroundColor: '#dbeafe', color: '#1e40af' };
  if (color.includes('amarill')) return { backgroundColor: '#fef9c3', color: '#854d0e' };
  if (color.includes('naranja')) return { backgroundColor: '#ffedd5', color: '#9a3412' };
  if (color.includes('blanc')) return { backgroundColor: '#ffffff', color: '#111827', boxShadow: 'inset 0 0 0 1px #d1d5db' };
  if (color.includes('transparent') || color.includes('cristal')) return { backgroundColor: '#ecfeff', color: '#155e75' };
  return { backgroundColor: '#f3f4f6', color: '#374151' };
};
const ClientRemitos: React.FC = () => {
  const [remitos, setRemitos] = useState<RemitoRow[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchRemitos = async () => {
      try {
        const response = await apiService.getMyReceipts({ page: 1, limit: 100 });
        setRemitos(response.receipts || []);
      } catch (error: any) {
        console.error('Error fetching remitos:', error);
        setError(error?.response?.data?.detail || 'No se pudieron cargar tus remitos. Intentá nuevamente.');
        setRemitos([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchRemitos();
  }, []);

  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString.replace(' ', 'T') + 'Z').toLocaleString('es-AR');
    } catch {
      return dateString;
    }
  };

  const handleDownload = async (id: number) => {
    try {
      setBusyId(id);
      const { blob, filename } = await apiService.downloadReceiptPdf(id);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } finally {
      setBusyId(null);
    }
  };

  const handlePrint = async (id: number) => {
    try {
      setBusyId(id);
      const { blob } = await apiService.downloadReceiptPdf(id);
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
    } finally {
      setBusyId(null);
    }
  };

  if (isLoading) {
    return (
      <div className="animate-pulse">
        <div className="h-8 bg-gray-300 rounded w-1/4 mb-6"></div>
        {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700" role="alert">
          {error}
        </div>
      )}
      <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-white p-6 rounded-lg shadow">
              <div className="h-4 bg-gray-300 rounded w-3/4 mb-2"></div>
              <div className="h-3 bg-gray-300 rounded w-1/2"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Remitos</h1>
          <p className="text-gray-600">Constancias de entrega de mercadería. Descargalas o imprimilas.</p>
        </div>
        <span className="text-sm text-gray-500">{remitos.length} remitos encontrados</span>
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700" role="alert">
          {error}
        </div>
      )}
      <div className="space-y-4">
        {remitos.map((remito) => (
          <div key={remito.id} className="bg-white rounded-lg shadow hover:shadow-md transition-shadow p-6">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">{remito.receiptNumber}</h3>
                <p className="text-sm text-gray-500">Entregado el {formatDate(remito.createdAt)}</p>
                {remito.items?.length ? (
                  <div className="mt-4 overflow-hidden rounded-lg border border-gray-200">
                    <table className="min-w-full text-sm">
                      <thead className="bg-gray-50 text-left text-gray-600"><tr><th className="px-4 py-2">Medida</th><th className="px-4 py-2">Color</th><th className="px-4 py-2 text-right">Packs de 50</th><th className="px-4 py-2 text-right">Bolsas</th></tr></thead>
                      <tbody className="divide-y divide-gray-100">{remito.items.map((item, index) => <tr key={`${item.productName}-${item.productColor}-${index}`}><td className="px-4 py-2 font-medium text-gray-900">{item.productName}</td><td className="px-4 py-2"><span className="inline-flex min-w-24 justify-center rounded-md px-2.5 py-1 text-xs font-bold" style={colorStyle(item.productColor)}>{item.productColor || 'Sin especificar'}</span></td><td className="px-4 py-2 text-right">{item.packs.toLocaleString('es-AR')}{item.looseBags ? ` + ${item.looseBags} sueltas` : ''}</td><td className="px-4 py-2 text-right">{item.quantity.toLocaleString('es-AR')}</td></tr>)}</tbody>
                    </table>
                  </div>
                ) : remito.concept ? (
                  <p className="mt-3 rounded-lg bg-gray-50 p-3 text-sm text-gray-600 whitespace-pre-line">{remito.concept}</p>
                ) : null}
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handlePrint(remito.id)}
                  disabled={busyId === remito.id}
                  className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-60"
                >
                  <Printer className="h-4 w-4" />
                  Imprimir
                </button>
                <button
                  onClick={() => handleDownload(remito.id)}
                  disabled={busyId === remito.id}
                  className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-60"
                >
                  <Download className="h-4 w-4" />
                  Descargar
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {!error && remitos.length === 0 && (
        <div className="w-full overflow-hidden rounded-xl border border-gray-200 bg-white px-4 py-12 text-center shadow-sm">
          <div className="mx-auto max-w-lg">
            <Truck className="mx-auto mb-4 h-16 w-16 text-gray-400" />
            <h3 className="mb-2 text-lg font-medium text-gray-900">Todavía no tenés remitos</h3>
            <p className="whitespace-normal break-words text-gray-600">Cuando se registre una entrega de mercadería a tu nombre, vas a poder verla e imprimirla acá.</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientRemitos;
